import { Dropdown, MultiSelect } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { DateRangeFilter } from '../fields/DateField';
import { ReferenceComboBox } from '../fields/ReferenceComboBox';
import { useReferencePickerPage } from '../fields/references';
import type { FilterDef } from '../resources';
import type { FilterValues } from './useFilters';

interface Choice {
  id: string;
  label: string;
}

/**
 * The filters of a generated list (tools/codegen) as dropdowns, each titled and prompting with its field name: lists
 * of values pick one or more, yes/no picks Any, Yes or No, references search the referenced records, dates take a
 * from–to range. Values are what the list API takes (see the backend's CrudFilter).
 */
export function ResourceFilters({
  filters,
  values,
  onChange,
}: {
  filters: readonly FilterDef[];
  values: FilterValues;
  onChange(values: FilterValues): void;
}) {
  return (
    <>
      {filters.map((filter) => (
        <FilterField
          key={filter.key}
          filter={filter}
          value={values[filter.key] ?? ''}
          onChange={(v) => onChange({ ...values, [filter.key]: v })}
        />
      ))}
    </>
  );
}

function FilterField({ filter, value, onChange }: { filter: FilterDef; value: string; onChange(value: string): void }) {
  const { t } = useTranslation();
  const id = `filter-${filter.key}`;
  const label = t(filter.label);

  if (filter.type === 'select') {
    const items: Choice[] = (filter.options ?? []).map((o) => ({ id: String(o.value), label: t(o.label) }));
    const chosen = new Set(value ? value.split(',') : []);
    return (
      <MultiSelect<Choice>
        id={id}
        titleText={label}
        label={label}
        items={items}
        itemToString={(o) => o?.label ?? ''}
        selectedItems={items.filter((o) => chosen.has(o.id))}
        onChange={({ selectedItems }) => onChange((selectedItems ?? []).map((o) => o.id).join(','))}
        selectionFeedback="fixed"
      />
    );
  }

  if (filter.type === 'toggle')
    return (
      <ChoiceDropdown
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        choices={[
          { id: 'true', label: t('admin.yes') },
          { id: 'false', label: t('admin.no') },
        ]}
      />
    );

  if (filter.type === 'reference')
    return <ReferenceFilter id={id} label={label} resourceKey={filter.ref} value={value} onChange={onChange} />;

  if (filter.type === 'date' || filter.type === 'datetime') {
    const [from = '', to = ''] = value.split('..');
    return (
      <DateRangeFilter
        id={id}
        legendText={label}
        from={from}
        to={to}
        onChange={(range) => onChange(range.from || range.to ? `${range.from}..${range.to}` : '')}
      />
    );
  }

  return null;
}

/** One choice or Any, as a dropdown titled and prompting with the field name. */
export function ChoiceDropdown({
  id,
  label,
  choices,
  value,
  onChange,
}: {
  id: string;
  label: string;
  choices: Choice[];
  value: string;
  onChange(value: string): void;
}) {
  const { t } = useTranslation();
  const any: Choice = { id: '', label: t('admin.filters.any') };
  const items = [any, ...choices];
  const selected = items.find((c) => c.id === value);
  return (
    <Dropdown<Choice>
      id={id}
      titleText={label}
      label={label}
      items={items}
      itemToString={(o) => o?.label ?? ''}
      // null (not undefined) keeps the dropdown controlled, so Reset shows the field name again.
      selectedItem={(selected?.id ? selected : null) as Choice}
      onChange={({ selectedItem }) => onChange(selectedItem?.id ?? '')}
    />
  );
}

/** A searchable dropdown over the referenced records; hidden when they can't be listed (no lookup path). */
function ReferenceFilter({
  id,
  label,
  resourceKey,
  value,
  onChange,
}: {
  id: string;
  label: string;
  resourceKey?: string;
  value: string;
  onChange(value: string): void;
}) {
  // The picker's own first page (shared with it, one request); it searches on the server when the resource is large (ADR 0099).
  const page = useReferencePickerPage(resourceKey);
  if (!page.isLoading && (page.data?.options.length ?? 0) === 0) return null;
  return (
    <ReferenceComboBox
      id={id}
      titleText={label}
      resourceKey={resourceKey}
      value={value || null}
      onChange={(next) => onChange(next ?? '')}
    />
  );
}
